import _ from 'lodash';
import { Channel } from 'scbk-services/config';
import { iconInfoList } from 'scbk-services/constants/iconInfoList';
import {
    FlattenMenuItem,
    iconInfoType,
    MenuField,
    MenuItem,
    MenuParameter,
    MenuTreeItem,
    SiteMapItem,
    SiteMapTreeItem,
    SubNavItemProps,
} from 'scbk-services/types/menu/menu';

/**
 * XML 메뉴 로더
 */
export class MenuLoader {
    sortByOrder = <T extends MenuItem>(a: T, b: T) => (a.order ?? 0) - (b.order ?? 0);

    constructor(
        private channel: string | Channel,
        private xml: string,
        private siteMapXml: string,
    ) {}

    /**
     * 메뉴정보를 로드한다.
     * @returns
     */
    public async load(): Promise<MenuTreeItem> {
        return this.parseMenuInfo(this.xml);
    }

    /**
     * 메뉴트리 정보를 기준으로 flatten 메뉴 목록을 가져온다.
     * @param menuItems
     * @param path
     * @returns
     */
    public async flatMenuTree(
        menuItems: MenuItem[],
        path: string[] = [],
    ): Promise<FlattenMenuItem[]> {
        const result: FlattenMenuItem[] = [];

        for (const menu of menuItems) {
            const currentPath = [...path, menu.name];

            result.push({
                ..._.omit(menu, ['children']),
                path: currentPath.join('/'),
            });

            if (menu.children) {
                const values = Object.values(menu.children);
                if (values) {
                    const children = await this.flatMenuTree(values, currentPath);
                    result.push(...children);
                }
            }
        }
        return result;
    }

    /**
     * XML 메뉴정보
     * @param xmlString
     * @returns
     */
    private parseMenuInfo(xmlString: string): MenuTreeItem {
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlString, 'text/xml');

        const rootNodes = xml.querySelectorAll('menus > menu');

        const menuTree: MenuTreeItem = {};
        Array.from(rootNodes).forEach((el, idx) => {
            const menu = this.parseMenu(el, idx);
            const ch = menu.channel;
            if (ch && ch.includes(this.channel as Channel)) {
                menuTree[menu.id] = menu;
            }
        });

        const sortedMenuTree = this.sortMenuTreeByOrder(menuTree);
        return sortedMenuTree;
    }

    /**
     * 메뉴정보를 파싱한다.
     * @param node 메뉴정보를 담고 있는 xml 요소
     * @param depth 트리 depth
     * @returns
     */
    private parseMenu(node: Element, idx: number = 1, depth: number = 1): MenuItem {
        const id = node.getAttribute('id') ?? '';
        const name = node.getAttribute('name') ?? '';
        const url = node.getAttribute('url') ?? '';
        const display = node.getAttribute('display') === 'true';
        const channelType = node.getAttribute('channelType') ?? '';
        const channel = this.getSupportedChannels(channelType);

        const fields = this.parseMenuField(node);
        const parameter = this.parseMenuParameter(node);

        const children: Record<string, MenuItem> = {};
        const childrenNodes = node.querySelectorAll(':scope > children > menu');
        if (childrenNodes) {
            childrenNodes.forEach((el, cIdx) => {
                const menu = this.parseMenu(el, cIdx, depth + 1);
                const ch = menu.channel;
                if (ch && ch.includes(this.channel as Channel)) {
                    children[menu.id] = menu;
                }
            });
        }

        return {
            id,
            name,
            url,
            display,
            channelType,
            channel,
            fields,
            parameter,
            children,
            depth,
            order: idx,
        };
    }

    /**
     * 메뉴 필드 정보를 파싱한다.
     * @param node 메뉴 Element
     * @returns 메뉴 필드 정보
     */
    private parseMenuField(node: Element): MenuField {
        const fieldNodes = node.querySelectorAll(':scope > fields > field');
        if (!fieldNodes || fieldNodes.length === 0) {
            return {};
        }

        const fields: MenuField = {};
        fieldNodes.forEach((field) => {
            const fieldName = field.getAttribute('name');
            const fieldValue = field.getAttribute('value');

            if (fieldName && fieldValue) {
                fields[fieldName] = fieldValue;
            }
        });

        return fields;
    }

    /**
     * 메뉴 파라미터 정보를 파싱한다.
     * @param node 메뉴 Element
     * @returns 메뉴 파라미터 정보
     */
    private parseMenuParameter(node: Element): MenuParameter {
        const parameterNodes = node.querySelectorAll(':scope > parameters > parameter');
        if (!parameterNodes || parameterNodes.length === 0) {
            return {};
        }

        const parameters: MenuParameter = {};
        parameterNodes.forEach((parameter) => {
            const parameterName = parameter.getAttribute('name');
            const parameterValue = parameter.getAttribute('value');

            if (parameterName && parameterValue) {
                parameters[parameterName] = parameterValue;
            }
        });

        return parameters;
    }

    /**
     * 사이트맵 정보를 로드한다.
     * @returns
     */
    public async siteMapLoad(flattenMenuList: FlattenMenuItem[]): Promise<SiteMapTreeItem> {
        return this.parseSiteMap(this.siteMapXml, flattenMenuList);
    }

    /**
     * 사이트맵 정보를 파싱한다.
     * @returns
     */
    private parseSiteMap(siteXml: string, flattenMenuList: FlattenMenuItem[]) {
        const parser = new DOMParser();
        const xml = parser.parseFromString(siteXml, 'text/xml');

        const siteMapMenu = Array.from(xml.querySelectorAll('sitemap > menu'));

        const menuTree: SiteMapTreeItem = {};
        Array.from(siteMapMenu).forEach((el) => {
            const menu = this.parseMenuSiteMap(el, 1, flattenMenuList);
            if (menu) {
                const ch = menu.channel;
                if (ch && ch.includes(this.channel as Channel)) {
                    menuTree[menu.id] = menu;
                }
            }
        });
        return menuTree;
    }

    /**
     * 사이트맵(메뉴정보)를 파싱한다.
     * @param el : 사이트맵 정보가 들어갈 요소
     * @param depth 트리 depth
     * @param 메뉴 정보를 depth 구분 없이 나래비한 배열
     * @returns
     */
    private parseMenuSiteMap(
        el: Element,
        depth: number = 1,
        flattenMenuList: FlattenMenuItem[],
    ): SiteMapItem | undefined {
        const node = flattenMenuList.find(
            (item: FlattenMenuItem) => item.id === el.getAttribute('id'),
        );

        if (node) {
            // 메뉴 정보 조회
            const id = el.getAttribute('id') ?? '';
            const name = _.isEmpty(el.getAttribute('name'))
                ? (node.name ?? '')
                : (el?.getAttribute('name') ?? '');
            const url = node.url ?? '';
            const display = true;
            const channelType = el.getAttribute('channelType') ?? ''; // sitemap channelType으로 확인
            const channel = this.getSupportedChannels(channelType);

            const fields = node.fields ?? {};
            const parameter = node.parameter ?? {};

            const children: Record<string, SiteMapItem> = {};

            const childrenNodes = el.querySelectorAll(':scope > children > menu');

            if (childrenNodes) {
                childrenNodes.forEach((childEl) => {
                    const menu = this.parseMenuSiteMap(childEl, depth + 1, flattenMenuList);
                    if (menu) {
                        const ch = menu.channel;
                        if (ch && ch.includes(this.channel as Channel)) {
                            children[menu.id] = menu;
                        }
                    }
                });
            }

            // icon 정보 조회
            const icon = el.getAttribute('icon') ?? '';
            // icon 1 depth filter
            const iconSubMenus = iconInfoList.find(
                (item: iconInfoType) => item.icon === icon && !_.isEmpty(icon),
            );
            // icon 2 depth filter
            const iconSubMenu = iconSubMenus?.subMenus.find(
                (item: SubNavItemProps) => item.subid === id,
            );

            // icon 경로 TODO: 카멜케이스 적용여부 확인 필요 : 권태민
            let iconpath = '';
            // icon folder 경로
            let folder = '';
            if (iconSubMenu) {
                iconpath = iconSubMenu.iconpath;
                folder = iconSubMenu.folder;
            }

            let menuSiteMap: SiteMapItem = {
                id,
                name,
                url,
                display,
                channelType,
                channel,
                fields,
                parameter,
                children,
                depth,
                icon,
                iconpath, // TODO : 나중에 대소문자 구분 필요한지 확인 필요
                folder,
            };

            return menuSiteMap;
        }
    }

    /**
     * 채널타입 문자열을 열거형 상수값으로 변환한다.
     * @param channelType 메뉴 지원 채널 타입 문자열 (01: 스마트뱅킹, 02: 모바일웹, 03: 인터넷뱅킹)
     * @returns
     */
    private getSupportedChannels(channelType: string): Channel[] | undefined {
        if (!channelType || channelType === '' || channelType === undefined) {
            return [];
        }

        const channelArray: Channel[] = [];

        if (channelType.indexOf('01') > -1) {
            channelArray.push(Channel.IB);
        }

        if (channelType.indexOf('02') > -1) {
            channelArray.push(Channel.MB);
        }

        if (channelType.indexOf('03') > -1) {
            channelArray.push(Channel.MW);
        }

        return channelArray;
    }

    private sortChildrenByOrder(children: MenuTreeItem): MenuTreeItem | undefined {
        if (!children) {
            return children;
        }

        return Object.values(children)
            .sort(this.sortByOrder)
            .reduce<MenuTreeItem>((acc, item) => {
                acc[item.id] = item;

                if (item.children) {
                    item.children = this.sortChildrenByOrder(item.children);
                }

                return acc;
            }, {});
    }

    private sortMenuTreeByOrder(menuTree: MenuTreeItem): MenuTreeItem {
        return Object.values(menuTree)
            .sort(this.sortByOrder)
            .reduce<MenuTreeItem>((acc, menu) => {
                acc[menu.id] = menu;

                if (menu.children) {
                    menu.children = this.sortChildrenByOrder(menu.children);
                }

                return acc;
            }, {});
    }
}
